import React, {
  useState,
  useEffect,
  useMemo,
  useImperativeHandle,
  forwardRef,
} from "react";

import { Grid, GridColumn, GridToolbar } from "@progress/kendo-react-grid";
import { DropDownButton } from "@progress/kendo-react-buttons";

import { ControlSpinner } from "shared/components/spinners/ControlSpinner";
import { Alert } from "react-bootstrap";

import { IsNullOrEmpty } from "shared/helpers/NullEmptyHelpers";

import GridExports from "../components/GridExports";
import { useClientGridDataState } from "./hooks/useClientGridDataState";
import { useExportClientGridData } from "./hooks/useExportClientGridData";

import { useFetchClientGridData } from "./hooks/useFetchClientGridData";
import { CreateEnhancedChildrenForFirefox } from "../helpers/CreateEnhancedChildrenForFirefox";

const SharedMainSelectableClientGrid = forwardRef((props, ref) => {
  const {
    dataItemKey,
    styleOverride,
    gridButtons,
    pageSize = 50, // this will be the default page size
    isSelectable = false,
    aggregateColumnSettings,
    /*
    NOTE: Aggregate values need to be sent back up to the parent;
    This is because aggregates are handled within a "kendo cell",
    and this grid component does not handle things on a "cell" level.
    */
    sendAggregatesParentCallback,
    onDataStateChangedCallback,
    gridDataStateResetProcessingInitiated,
    onGridDataStateResetCompleted,
    onGridIsLoadingStatusChanged,
    gridErrorHandlerCallback,
    // disableCache = false, // New parameter to control caching behavior
    useQueryOptions = null,
    enableExportAggregates = false, // This will enable the export of aggregates if set to true
    //dateFormattingColumns = [], // This will be an array of column names that need date formatting
  } = props;

  const defaultButtonStyleClasses =
    "k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary";

  const SELECTED_FIELD = "SelectedId";
  //const DATA_ITEM_KEY = dataItemKey;
  //const idGetter = getter(DATA_ITEM_KEY);

  const {
    exportColumns,
    exportFormat,
    runningExportData,
    fullExportData,
    InitiateExport: InitiateGridExport,
    ExportHasCompleted,
  } = useExportClientGridData(
    props.apiGetEndpoint,
    props.apiParameters,
    gridErrorHandlerCallback
  );

  const exportReportOptions = {
    exportFileNamePrefix: props.exportFileNamePrefix,
    noGridExportOption: props.noGridExportOption,
    generateReportButtonTitle: props.generateReportButtonTitle,
    generateReportForSelectablesCallback:
      props.generateReportForSelectablesCallback,
  };

  const _grid = React.useRef();

  let {
    data: clientGridData,
    isLoading: fetchClientGridDataIsLoading,
    isError,
    error,
    refetch,
  } = useFetchClientGridData(
    props.apiGetEndpoint,
    props.apiParameters,
    props.onDataRetrievedProcessor, //provide this to provide custom logic after data is retrieved
    props.gridErrorHandlerCallback,
    props.onGridSourceDataChangedCallback,
    props.dateFormattingColumns,
    useQueryOptions,
    // props.disableCache
    props.useQueryOptions
  );

  const {
    mainGridResultState,
    mainGridDataState,
    onMainGridDataStateChange,
    ResetGridDataState,
    onSelectionChange,
    SelectAll,
    UnSelectAll,
    GetSelectedGridDataKeys,
    GetSelectedGridData,
    GetExportData,
  } = useClientGridDataState(
    clientGridData,
    dataItemKey,
    pageSize,
    aggregateColumnSettings,
    sendAggregatesParentCallback,
    {
      singleSelect: props.singleSelect,
      initialSelectedKeys: props.initialSelectedKeys,
      onSelectionChangedCallback: props.onSelectionChangedCallback,
    }
  );
  const [headerCheckBox, setHeaderCheckBox] = useState(false);

  function onExportCompleted() {
    ExportHasCompleted();
  }

  function GetAllExportDataCallback(exportFormat) {
    // console.log("GetAllExportDataCallback clicked on clientgrid: ", exportData);
    // alert("GetAllExportDataCallback clicked on clientgrid: " + exportFormat);
    let exportData = GetExportData();
    InitiateGridExport(exportFormat, _grid.current.columns, exportData);
  }

  function shouldShowSpinner() {
    if (runningExportData) {
      return true;
    }

    return fetchClientGridDataIsLoading;
  }

  let showSpinner = shouldShowSpinner();

  useEffect(() => {
    if (onGridIsLoadingStatusChanged) {
      onGridIsLoadingStatusChanged(showSpinner);
    }
  }, [showSpinner]);

  // Fix: Call onGridSourceDataChangedCallback whenever clientGridData changes,
  // regardless of whether it came from cache or fresh fetch
  useEffect(() => {
    if (props.onGridSourceDataChangedCallback && clientGridData) {
      props.onGridSourceDataChangedCallback(clientGridData);
    }
  }, [clientGridData, props.onGridSourceDataChangedCallback]);

  //this will expose target methods to the parent component
  useImperativeHandle(ref, () => ({
    GetAllGridData() {
      return clientGridData;
    },
    GetGridFilteredData() {
      // Logic to get results of the grid after filtering

      //NOTE: Okay so there's a bug here if there are multiple pages of data;
      //if there are, then mainGridResultState only shows the first page of data
      //Fix this later
      // console.log(mainGridResultState.data);
      // console.log(mainGridDataState.data);
      // console.log(clientGridData);

      let gridData = mainGridResultState.data;

      if (IsNullOrEmpty(gridData)) {
        // if empty return the clientGridData, this can happen on initial loads before the grid sorts or filters are applied
        gridData = clientGridData;
      }

      return gridData;
    },
    GetAllFilteredData() {
      // Returns all filtered data across all pages (without pagination)
      return GetExportData();
    },
    ResetGridDataState() {
      ResetGridDataState();
    },
    GetSelectedGridDataKeys() {
      return GetSelectedGridDataKeys();
    },
    GetSelectedGridData() {
      return GetSelectedGridData();
    },
    UnselectAllRows() {
      UnSelectAll();
    },
    RefetchGridData() {
      return refetch();
    }
  }));

  //We want to memoize the grid state so that we don't trigger the onDataStateChangedCallback
  //every time the grid state changes. We only want to trigger it when the grid state changes
  const gridStateMemoed = useMemo(() => {
    return mainGridDataState;
  }, [mainGridDataState]);

  useEffect(() => {
    if (showSpinner) {
      return;
    }

    if (!onDataStateChangedCallback) return;

    //Purposely not passing the mainGridDataState to the parent component, as it should be accessible with the ref
    //if we pass it, someone may store in session and make the page heavy for no reason
    onDataStateChangedCallback();
  }, [showSpinner, gridStateMemoed]);

  const onHeaderSelectionChange = (event) => {
    const checkboxElement = event.syntheticEvent.target;
    const checked = checkboxElement.checked;

    setHeaderCheckBox(checked);

    if (checked) {
      SelectAll();
    } else {
      UnSelectAll();
    }

    //alert("onHeaderSelectionChange: " + checked);
  };

  const ShouldDisableToolbarButton = (
    shouldDisableWhenNoRecordSelectedOption
  ) => {
    // If the option is not true, don't disable
    if (shouldDisableWhenNoRecordSelectedOption !== true) return false;

    // If there are no records in the grid, disable the button
    if (
      !mainGridResultState ||
      !mainGridResultState.data ||
      mainGridResultState.data.length === 0
    )
      return true;

    // If any records are selected, enable the button
    var selectedKeys = GetSelectedGridDataKeys();
    if (selectedKeys.length === 0) return true;

    return false;
  };

  const getButtonDropdownOptions = (dropdownButtonOptions) => {
    // this should be a comma separated string, split it and remove any leading or trailing spaces
    let optionsArray = dropdownButtonOptions
      .split(",")
      .map((item) => item.trim());

    //now generate an array of objects that can be used to render the dropdown button
    let dropdownOptions = optionsArray.map((item) => {
      return { text: item, id: item };
    });

    return dropdownOptions;
  };

  const isDropdownButton = (dropdownButtonOptions) => {
    // return true/false depending on if the dropdownButtonOptions is a comma separated string with at least 2 values

    if (IsNullOrEmpty(dropdownButtonOptions)) return false;

    let optionsArray = dropdownButtonOptions
      .split(",")
      .map((item) => item.trim());

    if (optionsArray.length < 2) {
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (IsNullOrEmpty(gridDataStateResetProcessingInitiated)) return;
    if (IsNullOrEmpty(onGridDataStateResetCompleted)) return;

    if (!gridDataStateResetProcessingInitiated) return;

    //if we are here then we need to reset the grid data state
    ResetGridDataState();
    UnSelectAll(); //make sure no records are selected

    if (headerCheckBox) {
      setHeaderCheckBox(false);
    }

    onGridDataStateResetCompleted();
  }, [gridDataStateResetProcessingInitiated]);

  const enhancedChildren = CreateEnhancedChildrenForFirefox(props.children);

  //if error then return the error component and don't proceed
  if (isError && error.description) {
    return (
      <div>
        <Alert variant={"warning"} dismissible>
          {typeof error.description === 'object' ? JSON.stringify(error.description) : error.description}
        </Alert>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div
        className={
          shouldShowSpinner()
            ? "hidden-div"
            : "visible-element  row row-cols-auto"
        }
      >
        <GridExports
          noGridExportOption={exportReportOptions.noGridExportOption}
          exportFileNamePrefix={exportReportOptions.exportFileNamePrefix}
          exportEnabled={IsNullOrEmpty(mainGridResultState.data) ? false : true}
          exportData={fullExportData}
          exportFormat={exportFormat}
          exportDataColumns={exportColumns}
          exportInitiated={runningExportData}
          initiateExportCallback={GetAllExportDataCallback}
          exportCompletedCallback={onExportCompleted}
          aggregateColumnSettings={
            enableExportAggregates ? aggregateColumnSettings : null
          }
          dateFormattingColumns={props.dateFormattingColumns}
        />
      </div>

      {shouldShowSpinner() ? (
        <ControlSpinner />
      ) : (
        <React.Fragment>
          <Grid
            style={styleOverride ? styleOverride : { height: "700px" }}
            ref={_grid}
            data={mainGridResultState}
            filterable={true}
            sortable={true}
            groupable={true}
            pageable={true}
            dataItemKey={dataItemKey}
            selectedField={SELECTED_FIELD}
            selectable={
              isSelectable
                ? {
                  enabled: false,
                  drag: false,
                  cell: false,
                  mode: "multiple",
                }
                : false
            }
            onDataStateChange={(e) => {
              //when we change the data state, we need to make sure the header checkbox is unchecked as becasue if we for exasmple check the header and change to a new page we will not automatically select all records on the new page and therefore do not want a checked header checkbox with nothing selected
              if (headerCheckBox) {
                setHeaderCheckBox(false);
              }
              onMainGridDataStateChange(e);
            }}
            onSelectionChange={onSelectionChange}
            onHeaderSelectionChange={onHeaderSelectionChange}
            {...mainGridDataState}
          >
            <GridToolbar>
              {gridButtons &&
                gridButtons.map((thisButton) => (
                  <React.Fragment>
                    {isDropdownButton(thisButton.dropdownButtonOptions) ? (
                      <DropDownButton
                        text={thisButton.text}
                        textField="text"
                        items={getButtonDropdownOptions(
                          thisButton.dropdownButtonOptions
                        )}
                        //onItemClick={thisButton.callback}
                        onItemClick={(e) => {
                          thisButton.callback(e.item.id); //pass the id of the item clicked
                        }}
                        themeColor={"primary"}
                        disabled={ShouldDisableToolbarButton(
                          thisButton.disableWhenNoRecordSelected
                        )}
                      />
                    ) : (
                      <button
                        key={thisButton.text
                          .replace(/\s+/g, "")
                          .trim()
                          .toLowerCase()}
                        title={thisButton.text}
                        className={
                          thisButton.styleOverride || defaultButtonStyleClasses
                        }
                        disabled={ShouldDisableToolbarButton(
                          thisButton.disableWhenNoRecordSelected
                        )}
                        onClick={thisButton.callback}
                      >
                        {thisButton.text}
                      </button>
                    )}
                  </React.Fragment>
                ))}
            </GridToolbar>

            {isSelectable && (
              <GridColumn
                field={SELECTED_FIELD}
                width={"50px"}
                filterable={false}
                headerSelectionValue={
                  props.singleSelect ? undefined : headerCheckBox
                }
              />
            )}

            {enhancedChildren}
          </Grid>
        </React.Fragment>
      )}
    </React.Fragment>
  );
});
export default SharedMainSelectableClientGrid;

//This method takes in a currentArray, buttonText, and buttonCallback and returns a new array with the button added to the end of the currentArray
export function AddGridButton(
  currentArray,
  buttonText,
  buttonCallback,
  disableWhenNoRecordSelected = true,
  styleOverride = null
) {
  let newArrayItem = {
    text: buttonText,
    callback: buttonCallback,
    disableWhenNoRecordSelected: disableWhenNoRecordSelected,
    styleOverride: styleOverride,
  };

  let newArray = [...currentArray, newArrayItem];

  return newArray;
}
